import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getEtsyConfigSetById,
  updateEtsyConfigSet,
  getAllCurrencies,
  getAllMaterials,
  getAllTags,
  type UpdateEtsyConfigSetInput,
} from '@/lib/queries/configSets'
import toast from 'react-hot-toast'

/**
 * Etsy Config Set Edit page
 * Form for editing an Etsy config set
 */
export default function EtsyConfigSetEdit() {
  const { id, brandId } = useParams<{ id: string; brandId?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const etsyBasePath = brandId ? `/brands/${brandId}/integrations/etsy` : '/integrations/etsy'

  // Fetch config set
  const { data: configSet, isLoading, error } = useQuery({
    queryKey: ['etsyConfigSet', id],
    queryFn: () => getEtsyConfigSetById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  // Fetch related data
  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies'],
    queryFn: getAllCurrencies,
  })

  const { data: materials = [] } = useQuery({
    queryKey: ['materials'],
    queryFn: getAllMaterials,
  })

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getAllTags,
  })

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [shippingTemplateId, setShippingTemplateId] = useState('')
  const [taxonomyId, setTaxonomyId] = useState('')
  const [shopSectionId, setShopSectionId] = useState('')
  const [returnPolicyId, setReturnPolicyId] = useState('')
  const [currencyId, setCurrencyId] = useState<string | null>(null)
  const [whoMade, setWhoMade] = useState('i_did')
  const [whenMade, setWhenMade] = useState('made_to_order')
  const [isSupply, setIsSupply] = useState(false)
  const [processingMin, setProcessingMin] = useState<number>(1)
  const [processingMax, setProcessingMax] = useState<number>(3)
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Initialize form from config set data
  useEffect(() => {
    if (configSet) {
      setName(configSet.name || '')
      setDescription(configSet.description || '')
      setShippingTemplateId(configSet.shipping_template_id || '')
      setTaxonomyId(configSet.taxonomy_id || '')
      setShopSectionId(configSet.shop_section_id || '')
      setReturnPolicyId(configSet.return_policy_id || '')
      setCurrencyId(configSet.currency_code?._id || null)
      setWhoMade(configSet.who_made || 'i_did')
      setWhenMade(configSet.when_made || 'made_to_order')
      setIsSupply(configSet.is_supply || false)
      setProcessingMin(configSet.processing_min ?? 1)
      setProcessingMax(configSet.processing_max ?? 3)
      setSelectedMaterials(configSet.materials?.map((m) => m._id) || [])
      setSelectedTags(configSet.tags?.map((t) => t._id) || [])
    }
  }, [configSet])

  const { mutateAsync: doUpdate, isPending } = useMutation({
    mutationFn: (input: UpdateEtsyConfigSetInput) => updateEtsyConfigSet(id!, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['etsyConfigSet', id] })
      await queryClient.invalidateQueries({ queryKey: ['etsyConfigSets'] })
      navigate(`${etsyBasePath}/${id}`)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Name is required')
      return
    }

    if (!description.trim()) {
      toast.error('Description is required')
      return
    }

    if (selectedTags.length === 0) {
      toast.error('At least one tag is required')
      return
    }

    if (selectedTags.length > 13) {
      toast.error('Maximum 13 tags allowed')
      return
    }

    try {
      await doUpdate({
        name: name.trim(),
        description: description.trim(),
        shipping_template_id: shippingTemplateId.trim(),
        taxonomy_id: taxonomyId.trim(),
        shop_section_id: shopSectionId.trim(),
        return_policy_id: returnPolicyId.trim(),
        currency_code: currencyId,
        who_made: whoMade,
        when_made: whenMade,
        is_supply: isSupply,
        processing_min: processingMin,
        processing_max: processingMax,
        materials: selectedMaterials,
        tags: selectedTags,
      })
    } catch (error) {
      toast.error(`Failed to update config set: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleCancel = () => {
    navigate(`${etsyBasePath}/${id}`)
  }

  const toggleMaterial = (materialId: string) => {
    setSelectedMaterials((prev) =>
      prev.includes(materialId) ? prev.filter((id) => id !== materialId) : [...prev, materialId]
    )
  }

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) => {
      if (prev.length >= 13 && !prev.includes(tagId)) {
        toast.error('Maximum 13 tags allowed')
        return prev
      }
      return prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-900">Loading config set...</h1>
          </div>
        </header>
      </div>
    )
  }

  if (error || !configSet) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-900">Config Set Not Found</h1>
          </div>
        </header>
        <main className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              {error instanceof Error ? error.message : 'Config set not found'}
            </p>
            <Link
              to={etsyBasePath}
              className="mt-4 inline-block text-blue-600 hover:underline font-medium"
            >
              ← Back to Config Sets
            </Link>
          </div>
        </main>
      </div>
    )
  }

  const whoMadeOptions = [
    { value: 'i_did', label: 'I did' },
    { value: 'collective', label: 'A member of my shop did' },
    { value: 'someone_else', label: 'Another company or person did' },
  ]

  const whenMadeOptions = [
    { value: 'made_to_order', label: 'Made to order' },
    { value: '2020s', label: '2020s' },
    { value: '2010s', label: '2010s' },
    { value: '2000s', label: '2000s' },
    { value: '1990s', label: '1990s' },
    { value: '1980s', label: '1980s' },
    { value: '1970s', label: '1970s' },
    { value: '1960s', label: '1960s' },
    { value: '1950s', label: '1950s' },
    { value: '1940s', label: '1940s' },
    { value: '1930s', label: '1930s' },
    { value: '1920s', label: '1920s' },
    { value: '1910s', label: '1910s' },
    { value: '1900s', label: '1900s' },
    { value: '1800s', label: '1800s' },
    { value: '1700s', label: '1700s' },
    { value: '1600s', label: '1600s' },
    { value: '1500s', label: '1500s' },
    { value: '1400s', label: '1400s' },
    { value: '1300s', label: '1300s' },
    { value: '1200s', label: '1200s' },
    { value: '1100s', label: '1100s' },
    { value: '1000s', label: '1000s' },
    { value: 'pre_1000', label: 'Pre-1000' },
  ]

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`${etsyBasePath}/${id}`}
              className="text-gray-600 hover:text-gray-900"
              aria-label="Back to config set detail"
            >
              ←
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Edit Config Set</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Basic Information */}
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Description *
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          {/* Etsy Configuration */}
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Etsy Configuration</h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="shipping_template_id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Shipping Template ID *
                </label>
                <input
                  type="text"
                  id="shipping_template_id"
                  value={shippingTemplateId}
                  onChange={(e) => setShippingTemplateId(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="taxonomy_id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Taxonomy ID *
                </label>
                <input
                  type="text"
                  id="taxonomy_id"
                  value={taxonomyId}
                  onChange={(e) => setTaxonomyId(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="shop_section_id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Shop Section ID *
                </label>
                <input
                  type="text"
                  id="shop_section_id"
                  value={shopSectionId}
                  onChange={(e) => setShopSectionId(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="return_policy_id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Return Policy ID *
                </label>
                <input
                  type="text"
                  id="return_policy_id"
                  value={returnPolicyId}
                  onChange={(e) => setReturnPolicyId(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="currency_code"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Currency *
                </label>
                <select
                  id="currency_code"
                  value={currencyId || ''}
                  onChange={(e) => setCurrencyId(e.target.value || null)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select currency</option>
                  {currencies.map((currency) => (
                    <option key={currency._id} value={currency._id}>
                      {currency.code} ({currency.symbol}) - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Product Details */}
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="who_made" className="block text-sm font-medium text-gray-700 mb-2">
                  Who Made *
                </label>
                <select
                  id="who_made"
                  value={whoMade}
                  onChange={(e) => setWhoMade(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {whoMadeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="when_made" className="block text-sm font-medium text-gray-700 mb-2">
                  When Made *
                </label>
                <select
                  id="when_made"
                  value={whenMade}
                  onChange={(e) => setWhenMade(e.target.value)}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {whenMadeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex items-center min-h-[48px]">
                  <input
                    type="checkbox"
                    id="is_supply"
                    checked={isSupply}
                    onChange={(e) => setIsSupply(e.target.checked)}
                    className="w-5 h-5 mr-2"
                  />
                  <label htmlFor="is_supply" className="text-sm font-medium text-gray-700">
                    Is Supply
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Processing Time
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label
                      htmlFor="processing_min"
                      className="block text-xs text-gray-600 mb-1"
                    >
                      Min (days)
                    </label>
                    <input
                      type="number"
                      id="processing_min"
                      value={processingMin}
                      onChange={(e) => setProcessingMin(Number(e.target.value))}
                      min={0}
                      max={30}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="processing_max"
                      className="block text-xs text-gray-600 mb-1"
                    >
                      Max (days)
                    </label>
                    <input
                      type="number"
                      id="processing_max"
                      value={processingMax}
                      onChange={(e) => setProcessingMax(Number(e.target.value))}
                      min={0}
                      max={30}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Materials & Tags */}
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Materials & Tags</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Materials (max 13)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedMaterials.map((materialId) => {
                    const material = materials.find((m) => m._id === materialId)
                    return (
                      <span
                        key={materialId}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm flex items-center gap-2"
                      >
                        {material?.name || materialId}
                        <button
                          type="button"
                          onClick={() => toggleMaterial(materialId)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ×
                        </button>
                      </span>
                    )
                  })}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      toggleMaterial(e.target.value)
                      e.target.value = ''
                    }
                  }}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Add material...</option>
                  {materials
                    .filter((m) => !selectedMaterials.includes(m._id))
                    .map((material) => (
                      <option key={material._id} value={material._id}>
                        {material.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (1-13 required) *
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedTags.map((tagId) => {
                    const tag = tags.find((t) => t._id === tagId)
                    return (
                      <span
                        key={tagId}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm flex items-center gap-2"
                      >
                        {tag?.name || tagId}
                        <button
                          type="button"
                          onClick={() => toggleTag(tagId)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          ×
                        </button>
                      </span>
                    )
                  })}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      toggleTag(e.target.value)
                      e.target.value = ''
                    }
                  }}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Add tag...</option>
                  {tags
                    .filter((t) => !selectedTags.includes(t._id))
                    .map((tag) => (
                      <option key={tag._id} value={tag._id}>
                        {tag.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isPending}
              className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
